import { AlertTriangle, HelpCircle, X } from 'lucide-react';

interface Props {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'default' | 'warning' | 'danger';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Gjenbrukbar bekreftelses-dialog. Erstatter native window.confirm() —
 * gir tydeligere UX og hindrer utilsiktede klikk.
 */
export default function ConfirmDialog({
  open, title, message, confirmLabel = 'Bekreft', cancelLabel = 'Avbryt',
  variant = 'default', onConfirm, onCancel
}: Props) {
  if (!open) return null;

  const variantStyles = {
    default: { icon: HelpCircle, iconColor: 'text-kf-primary', btn: 'bg-kf-primary hover:bg-kf-primary-dark' },
    warning: { icon: AlertTriangle, iconColor: 'text-amber-600', btn: 'bg-amber-600 hover:bg-amber-700' },
    danger: { icon: AlertTriangle, iconColor: 'text-red-600', btn: 'bg-red-600 hover:bg-red-700' }
  };
  const style = variantStyles[variant];
  const Icon = style.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onCancel}
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className={`flex-shrink-0 ${style.iconColor}`}>
              <Icon className="h-6 w-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-700 mt-2 whitespace-pre-line">{message}</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-900 flex-shrink-0"
              aria-label="Lukk"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="border-t px-6 py-3 flex justify-end gap-2 bg-gray-50 rounded-b-lg">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded font-medium"
          >
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm text-white rounded font-medium ${style.btn}`}
            autoFocus
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
